import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from 'react';
import { useLocation } from 'react-router-dom';

import { Box } from '@mui/material';
import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles';
import MUIDataTable from 'mui-datatables';

import { useTranslation } from 'react-i18next';

import { connect } from 'react-redux';
import { useHistory } from 'react-router';

import { trackEvent } from '@intercom/messenger-js-sdk';

import { CallMergeOutlined, SearchRounded } from '@mui/icons-material';
import { clientActions } from '../../store/actions/client.actions';

import DeleteIcon from '@/shared/assets/icons/delete.svg?react';
import EditIcon from '@/shared/assets/icons/edit.svg?react';
import AddClientIcon from '@/shared/assets/icons/sidenav/practitioners.svg?react';

import useDebounce from '../../hooks/useDebounce';
import { useTableFilterConfig } from '../../hooks/useTableFilterConfig';
import { useInitTableSearchParams } from '../../hooks/useInitTableSearchParams';
import { useTableFiltersSearchParams } from '../../hooks/useTableFiltersSearchParams';
import { useIntercomComponentOnOpenEventTrack } from '../../hooks/useIntercomComponentOnOpenEventTrack';

import { clientService } from '../../services/clients';

import {
  classNames,
  getTransformedLogicTableFilterValue,
  mapOrderToSort,
} from '../../utils';
import { showToast } from '../../utils/toast';
import { DEFAULT_FILTER_KEYS } from '../../utils/config';
import getMuiTheme from '../../utils/MUI-Style';
import tableConfig from '../../utils/MUI-table-config';
import { checkRouteAccess } from '../../utils/check-access';
import muiComponents from '../../utils/MUI-table-components';
import translateClientName from '../../utils/TranslatedPossibleClientName';
import { muiTableLoadingSkeleton } from '../../utils/MUI-table-loading-skaleton';

import { ClientSelect } from './ClientSelect';
import CreateClientForm from './CreateClientForm';
import { EditClientForm } from './EditClientForm';
import CustomDateSortHeader from '../Sessions/CustomDateSortHeader';
import { ModalUI } from '../UI/Modal/Modal';
import { ButtonUi } from '../UI/Button/Button';
import { FormModal } from '../UI/FormModal/FormModal';
import TableHeader from '../UI/TableHeader/TableHeader';
import { MergeDialog } from '../UI/MergeDialog/MergeDialog';
import { DataCell } from '../UI/MuiDatatable/DataCell/DataCell';
import { deleteConfirmationStyle } from '../UI/SimpleModalContent/style';
import { ClientAvatar } from '../UI/MuiDatatable/ClientAvatar/ClientAvatar';
import SimpleModalContent from '../UI/SimpleModalContent/SimpleModalContent';
import { CustomMuiDataTableFilter } from '../UI/MuiDatatable/CustomMuiDataTableFilter/CustomMuiDataTableFilter';
import { CustomMuiSessionsPerMonth } from '../UI/MuiDatatable/CustomMuiSessionsPerMonth/CustomMuiSessionsPerMonth';
import { CustomMuiDataTableBodyButton } from '../UI/MuiDatatable/CustomMuiDataTableBodyButton/CustomMuiDataTableBodyButton';

const ClientsTable = function ({
  authentication,
  clients: clientsReducer,
  location,
  getAllClients,
  clearClients,
  deleteClient,
  mergeClientAction,
}) {
  const { t } = useTranslation();
  const history = useHistory();
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const paramPage = Number(params.get('page'));
  const paramLimit = Number(params.get('limit')) || 10;
  const paramSearch = params.get('searchQuery') || '';
  const paramOrder = params.get('order');
  const paramSortType = paramOrder?.split(' ')?.[0];
  const paramDirection = paramOrder?.split(' ')?.[1];
  const paramStartScore = params.get('startScore')?.split(',')?.map(Number);
  const paramDecrease = params.get('decrease')?.split(',')?.map(Number);
  const paramEndScore = params.get('endScore')?.split(',')?.map(Number);

  const paramTotalSessions = params.get('totalSessions');
  const paramTotalSessionsSign = Object.keys(
    JSON.parse(paramTotalSessions) || {},
  )?.[0];
  const paramTotalSessionsValue =
    JSON.parse(paramTotalSessions)?.[paramTotalSessionsSign];

  const paramMonthlySessions = params.get('monthlySession');
  const paramMonthlySessionsSign = Object.keys(
    JSON.parse(paramMonthlySessions) || {},
  )?.[0];
  const paramMonthlySessionsValue =
    JSON.parse(paramMonthlySessions)?.[paramMonthlySessionsSign];

  const { deleteConfirmation } = deleteConfirmationStyle;

  const [page, setPage] = useState(paramPage / paramLimit || 0);
  const [limit, setLimit] = useState(paramLimit);
  const [currentSort, setCurrentSort] = useState(
    mapOrderToSort(paramDirection) || 'default',
  );
  const [currentSortType, setCurrentSortType] = useState(paramSortType || '');
  const [searchText, setSearchText] = useState(paramSearch);
  const [debouncedSearchTerm, setDebouncedValue] = useDebounce(searchText, 500);

  const [filterOpen, setFilterOpen] = useState(false);

  const [clientFilters, setClientFilters] = useState({
    ...(!!paramTotalSessions && {
      sessions: {
        value: paramTotalSessionsValue,
        sign: paramTotalSessionsSign,
        touched: true,
      },
    }),
    ...(!!paramMonthlySessions && {
      monthly_sessions: {
        value: paramMonthlySessionsValue,
        sign: paramMonthlySessionsSign,
        touched: true,
      },
    }),
    ...(!!paramStartScore && {
      start_score: {
        value: paramStartScore,
      },
    }),
    ...(!!paramDecrease && {
      decrease: {
        value: paramDecrease,
      },
    }),
    ...(!!paramEndScore && {
      end_score: {
        value: paramEndScore,
      },
    }),
  });

  const tableRef = useRef();

  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [deleteModalIsOpen, setDeleteModalIsOpen] = useState(false);
  const [mergeModalIsOpen, setMergeModalIsOpen] = useState(false);

  const [clientToEdit, setClientToEdit] = useState(null);
  const [clientToDelete, setClientToDelete] = useState(null);
  const [clientFromMerge, setClientFromMerge] = useState(null);

  const [autoCorrectPage, setAutoCorrectPage] = useState(false);

  const [toRedirect, setToRedirect] = useState(false);

  const { clients, isLoading, totalClients } = clientsReducer;

  /**
   * Check does user has permision to be on this page
   */
  useEffect(() => {
    const { foundUser, roles, userIsAdmin } = authentication;
    checkRouteAccess(location.pathname, foundUser, roles, userIsAdmin, history);
  }, []);

  /**
   * Opens the filter's modal and grabs data for the filters
   */
  const onOpenFilterModal = useCallback(() => {
    setFilterOpen(true);
  }, []);

  /**
   * Generates filters
   */
  const clientFilter = useMemo(() => {
    let order = '';
    let direction = '';
    let searchQuery = '';
    let isAnonymous = false;

    const startScore = clientFilters?.start_score?.value;
    const endScore = clientFilters?.end_score?.value;
    const decrease = clientFilters?.decrease?.value;

    const totalSessions = getTransformedLogicTableFilterValue(
      clientFilters,
      'sessions',
    );

    const monthlySession = getTransformedLogicTableFilterValue(
      clientFilters,
      'monthly_sessions',
    );

    direction = mapOrderToSort(currentSort);

    if (currentSort && currentSortType && direction !== 'default')
      order = `${currentSortType} ${direction}`;

    if (debouncedSearchTerm) {
      searchQuery = `${debouncedSearchTerm?.toLowerCase()}`;
      if (t('treatments.anon-client-name').toLowerCase().includes(searchQuery))
        isAnonymous = true;
      trackEvent('table-search', {
        table_name: 'Clients',
      });
    }

    return {
      page: page * limit || 0,
      limit,
      order,
      searchQuery,
      isAnonymous,
      startScore,
      endScore,
      totalSessions,
      monthlySession,
      decrease,
    };
  }, [
    t,
    page,
    limit,
    currentSort,
    currentSortType,
    clientFilters,
    debouncedSearchTerm,
  ]);

  /**
   * Gets all Clients on filters changes
   */
  useEffect(() => {
    const {
      page,
      limit,
      order,
      searchQuery,
      isAnonymous,
      startScore,
      endScore,
      totalSessions,
      monthlySession,
      decrease,
    } = clientFilter;
    getAllClients(
      page,
      limit,
      order,
      searchQuery,
      isAnonymous,
      startScore,
      endScore,
      totalSessions,
      monthlySession,
      decrease,
    );
  }, [getAllClients, clientFilter]);

  /**
   * Refresh teams data
   */
  const refreshCurrentData = useCallback(() => {
    const {
      page,
      limit,
      order,
      searchQuery,
      isAnonymous,
      startScore,
      endScore,
      totalSessions,
      monthlySession,
      decrease,
    } = clientFilter;
    getAllClients(
      page,
      limit,
      order,
      searchQuery,
      isAnonymous,
      startScore,
      endScore,
      totalSessions,
      monthlySession,
      decrease,
    );
  }, [getAllClients, clientFilter]);

  /**
   * Open edit / create client modal
   */
  const openModal = () => {
    setModalIsOpen(true);
  };

  /**
   * Close edit / create client modal
   */
  const closeModal = useCallback(() => {
    if (toRedirect) {
      history.push('/admin/dashboard');
    }
    setModalIsOpen(false);
    refreshCurrentData();
    setClientToEdit(null);
  }, [history, refreshCurrentData, toRedirect]);

  /**
   * Open delete modal
   */
  const onOpenDeleteModal = useCallback((event, id) => {
    event.stopPropagation();
    setClientToDelete(id);
    setDeleteModalIsOpen(true);
    trackEvent('start-client-deletion');
  }, []);

  /**
   * Close delete modal
   */
  const onCloseDeleteModal = useCallback(() => setDeleteModalIsOpen(false), []);

  /**
   * Open merge clients modal
   * @param id client's to merge id
   */
  const mergeClients = useCallback(
    (id) => {
      setMergeModalIsOpen(true);
      const foundClient = clients.find((client) => client.id === id);
      setClientFromMerge(foundClient);
    },
    [clients],
  );

  /**
   * Close merge modal
   */
  const onCloseMergeModal = useCallback(() => {
    setMergeModalIsOpen(false);
    refreshCurrentData();
    setClientFromMerge(null);
  }, [refreshCurrentData]);

  /**
   * Open edit client modal
   * @param event native event
   * @param id client's to edit id
   */
  const editClient = useCallback(
    (event, id) => {
      event.stopPropagation();
      setClientToEdit(clients.find((item) => item.id === id));
      setModalIsOpen(true);
    },
    [clients],
  );

  /**
   * Delete client handler
   */
  const onDeleteClient = useCallback(async () => {
    await deleteClient(clientToDelete);
    refreshCurrentData();
    setDeleteModalIsOpen(false);
  }, [deleteClient, clientToDelete, refreshCurrentData]);

  /**
   * Merge clients action
   * @param fromMergeId Client's Id to merge from
   * @param toMergeId Client's Id to merge into
   */
  const onMergeClient = useCallback(
    async (fromMergeId, toMergeId) => {
      await mergeClientAction(fromMergeId, toMergeId);
    },
    [mergeClientAction],
  );

  /**
   * Sort handler
   * @param type sort property
   */
  const handleSort = useCallback(
    (type) => {
      let nextSort;
      if (currentSort === 'down') nextSort = 'up';
      else if (currentSort === 'up') nextSort = 'default';
      else if (currentSort === 'default') nextSort = 'down';

      setCurrentSortType(type);
      setCurrentSort(nextSort);
    },
    [currentSort],
  );

  const onOpenDetails = useCallback(
    (rowData) => {
      const id = rowData[0]?.props?.children;
      const name = encodeURIComponent(
        rowData[1]?.props?.children?.props?.clientName,
      );

      history.push({
        pathname: `/admin/clients/${id}`,
        search: `clientId=${id}&isAnonymous=false&limit=10&order=start_date%20DESC&page=0`,
        state: {},
      });
    },
    [history],
  );

  /**
   * Init table's options
   */
  const options = useMemo(
    () => ({
      serverSide: true,
      page,
      rowsPerPage: limit,
      count: totalClients,
      onChangePage: (currentPage) => {
        setPage(currentPage);
      },
      onChangeRowsPerPage: (numberOfRows) => {
        const { page: offset, limit } = clientFilter;
        const showedRows = offset + limit;
        const pagesLimit = Math.ceil(totalClients / numberOfRows);
        if (showedRows <= numberOfRows && page !== 0) {
          const newPage = Math.floor(numberOfRows / showedRows) - 1;
          if (newPage >= pagesLimit) setPage(pagesLimit - 1);
          else setPage(newPage);
        } else if (showedRows > numberOfRows) {
          const newPage = Math.floor(showedRows / numberOfRows);
          if (newPage >= pagesLimit) setPage(pagesLimit - 1);
          else setPage(newPage);
        }
        setLimit(numberOfRows);
      },
      onSearchChange: (text) => setSearchText(text || ''),
      searchText,
      searchAlwaysOpen: true,
    }),
    [page, limit, totalClients, searchText, clientFilter],
  );

  /**
   * Generates table rows
   * @param dataToShow server data to transform
   */
  const createViewItemListForClients = (dataToShow) =>
    dataToShow.map((item) => {
      const { first, last, sum, monthSum, name, id } = item;
      const clientName = name || item.Naam;
      return {
        id: <DataCell>{id}</DataCell>,
        [t('forms.client_name')]: (
          <DataCell>
            <ClientAvatar
              clientName={translateClientName(clientName)}
              seedId={id}
            />
          </DataCell>
        ),
        [t('forms.begin_score')]: <DataCell type='numerics'>{first}</DataCell>,
        [t('forms.end_score')]: <DataCell type='numerics'>{last}</DataCell>,
        [t('forms.delta_client')]: (
          <DataCell type='numerics'>{last - first}</DataCell>
        ),
        [t('forms.total.sessions_this_month')]: (
          <CustomMuiSessionsPerMonth sum={sum || 0} monthSum={monthSum || 0} />
        ),
      };
    });

  /**
   * Generates export CSV data
   * @param data server data to transform
   */
  const generateExportData = (data) =>
    data.map((item) => {
      const { first, last, sum, monthSum, name, id } = item;
      const clientName = name || item.Naam;
      return {
        id,
        [t('forms.client_name')]: translateClientName(clientName),
        [t('forms.begin_score')]: first || 'null',
        [t('forms.end_score')]: last || 'null',
        [t('forms.delta_client')]: (last || 0) - (first || 0),
        [t('forms.total.sessions_this_month')]: `${sum || 0} (${
          monthSum || 0
        })`,
      };
    });

  /**
   * Render talbe haed
   */
  const renderHead = useCallback(
    (mappedSortTypes, key) => {
      const sortType = mappedSortTypes[key];
      return (
        <CustomDateSortHeader
          key={`sort-header-${key}`}
          currentSort={currentSortType === sortType ? currentSort : 'default'}
          label={key}
          type={sortType}
          handleSort={handleSort}
        />
      );
    },
    [currentSort, currentSortType, handleSort],
  );

  /**
   * Render talbe actions
   */
  const renderActions = useCallback(
    (value, tableMeta, updateValue) => (
      <CustomMuiDataTableBodyButton
        firstButtonIcon={<EditIcon />}
        secondButtonIcon={<DeleteIcon />}
        additionalButtons={[
          <ButtonUi
            key='client-details'
            isIconBtn
            className='table-action-btn primary details-btn'
            size='small'
            onClickHandler={() => onOpenDetails(tableMeta.rowData)}
          >
            <SearchRounded />
          </ButtonUi>,
          <ButtonUi
            key='clients-merge'
            isIconBtn
            className='table-action-btn primary merge-btn'
            size='small'
            onClickHandler={() =>
              mergeClients(tableMeta.rowData[0]?.props?.children)
            }
          >
            <CallMergeOutlined />
          </ButtonUi>,
        ]}
        onPrimaryActionButton={(e) =>
          editClient(e, tableMeta.rowData[0]?.props?.children)
        }
        onSecondaryActionButton={(e) =>
          onOpenDeleteModal(e, tableMeta.rowData[0]?.props?.children)
        }
      />
    ),
    [editClient, onOpenDeleteModal, onOpenDetails],
  );

  /**
   * Generates table attributes
   */
  const createTableAttributes = useCallback(
    (dataToShow) => {
      const mappedSortTypes = {
        [t('forms.client_name')]: 'name',
        [t('forms.total.sessions_this_month')]: 'sessions',
      };
      const newAttributes = Object.keys(dataToShow[0]).map((key) => {
        const keysToHide = ['id', 'organizationId', 'teamId'];
        if (keysToHide.includes(key)) {
          return {
            name: key,
            options: { display: false, sort: false },
          };
        }
        if (mappedSortTypes[key]) {
          return {
            name: key,
            options: {
              customHeadRender: () => renderHead(mappedSortTypes, key),
            },
          };
        }

        return {
          name: key,
          options: { sort: false },
        };
      });

      newAttributes.push({
        name: '',
        options: {
          filter: false,
          sort: false,
          empty: true,
          viewColumns: false,
          setCellProps: (value) => ({
            className: 'action',
          }),
          customBodyRender: (value, tableMeta, updateValue) =>
            renderActions(value, tableMeta, updateValue),
        },
      });
      return newAttributes;
    },
    [t, renderHead, renderActions],
  );

  // Leave the JSON.parse/JSON.stringify here to create a deepcopy of clients.
  let dataToShow = JSON.parse(JSON.stringify(clients || []));
  let attributes = [];

  if (dataToShow.length > 0) {
    dataToShow = createViewItemListForClients(dataToShow);
    attributes = createTableAttributes(dataToShow);
  }

  const getExportedData = useCallback(async () => {
    const {
      order,
      searchQuery,
      isAnonymous,
      startScore,
      endScore,
      totalSessions,
      monthlySession,
      decrease,
    } = clientFilter;
    try {
      const dataToExport = await clientService.getAllClientsWithStats(
        0,
        totalClients,
        order,
        searchQuery,
        isAnonymous,
        startScore,
        endScore,
        totalSessions,
        monthlySession,
        decrease,
      );
      return generateExportData(dataToExport?.data?.clients);
    } catch (error) {
      showToast(t('errors.get.clients'), 'error', 3000);
    }
  }, [totalClients, clientFilter, t]);

  /**
   * Generates the table filter's config
   */
  const filtersConfig = useTableFilterConfig('client');

  /**
   * Closes the filter's modal and save data for the filters
   */
  const onSaveFilters = useCallback((filters) => setClientFilters(filters), []);

  /**
   * Checks does table have data
   */
  const isDataTableEmpty = dataToShow.length <= 0;

  /**
   * Flag to checks does filters active
   */
  const isFilters =
    Object.keys(
      Object.entries(clientFilters || {}).filter(
        ([k]) => !DEFAULT_FILTER_KEYS.includes(k),
      ),
    )?.length > 0;

  /**
   * mui datatable optional class names
   */
  const muiTableMods = {
    loading: isLoading,
    'no-data': isDataTableEmpty,
    filters: isFilters,
  };

  /**
   * Sets table current active filters based on the table search params
   */
  useTableFiltersSearchParams(
    [
      page,
      setPage,
      limit,
      setLimit,
      currentSort,
      setCurrentSort,
      currentSortType,
      setCurrentSortType,
      '',
      setSearchText,
      debouncedSearchTerm,
      setDebouncedValue,
    ],
    [clientFilters, setClientFilters],
  );

  /**
   * Sets table search params based on the current active filters
   */
  useInitTableSearchParams(
    clientFilter,
    [autoCorrectPage, setAutoCorrectPage],
    [],
  );

  /**
   * Reset page for search if it's not the first page and there is no results
   */
  useEffect(() => {
    const pagesLimit = Math.ceil(totalClients / limit);
    if (page > pagesLimit && debouncedSearchTerm) {
      setAutoCorrectPage(true);
      setPage(0);
    }
  }, [debouncedSearchTerm, limit, page, totalClients]);

  /**
   * Reset page for existings filters if it's not the first page and there is no results
   */
  useEffect(() => {
    const definedLoading = isLoading !== undefined && !isLoading;
    if (isFilters && dataToShow.length <= 0 && definedLoading && page > 0) {
      setAutoCorrectPage(true);
      setPage(0);
    }
  }, [dataToShow, isFilters, page, isLoading]);

  /**
   * Table's query handler
   */
  useEffect(() => {
    if (location.search) {
      const params = new URLSearchParams(location.search);
      const isClientCreation = Boolean(params.get('create'));
      setToRedirect(Boolean(params.get('redirectTo')));
      if (isClientCreation) setModalIsOpen(true);
    }
  }, [location]);

  useIntercomComponentOnOpenEventTrack('page-visit', {
    name: 'Clients',
  });

  /**
   * Clears clients on leaving the page
   */
  useEffect(
    () => () => {
      clearClients();
    },
    [],
  );

  return (
    <Box>
      <TableHeader
        buttonTitle='buttons.create.client'
        onClickCallback={openModal}
        title='clients.title'
        subTitle='clients.subtitle'
        btnIcon={<AddClientIcon className='practitioner-icon' />}
      />
      <Box className='table-container'>
        <StyledEngineProvider injectFirst>
          <ThemeProvider
            theme={getMuiTheme(
              {},
              {
                '&:nth-of-type(7)': {
                  width: 100,
                },
              },
            )}
          >
            <MUIDataTable
              ref={tableRef}
              data={isLoading ? muiTableLoadingSkeleton('body') : dataToShow}
              columns={
                isLoading ? muiTableLoadingSkeleton('header') : attributes
              }
              options={tableConfig(
                options,
                isLoading,
                isDataTableEmpty,
                tableRef,
                isFilters,
                onOpenFilterModal,
                getExportedData,
                'Clients',
              )}
              components={muiComponents(clientFilters, setClientFilters)}
              className={classNames(
                'mui-data-table clients-table search-open',
                muiTableMods,
              )}
              title=''
            />
          </ThemeProvider>
        </StyledEngineProvider>
      </Box>
      {/* Merge Clients modal */}
      <ModalUI
        modalSx={deleteConfirmation}
        open={mergeModalIsOpen}
        isCloseBtn={false}
        onCloseHandler={onCloseMergeModal}
        className='confirmation-modal merge-clients-modal'
        modalTitle={t('alerts.titles.merge-clients')}
      >
        <MergeDialog
          onMerge={onMergeClient}
          fromMergeId={clientFromMerge?.id}
          fromMergeName={clientFromMerge?.name}
          closeModal={onCloseMergeModal}
          mergeFromTitle={t('clients.current')}
          AutocompleteComponent={
            <ClientSelect forceClientFetch excludeIds={[clientFromMerge?.id]} />
          }
        />
      </ModalUI>
      {/* Edit / create client modal */}
      {modalIsOpen && (
        <FormModal
          isOpen={modalIsOpen}
          title={
            clientToEdit ? t('buttons.edit.client') : t('buttons.create.client')
          }
          setOpen={setModalIsOpen}
          onClose={closeModal}
        >
          {clientToEdit ? (
            <EditClientForm
              closeModal={closeModal}
              clientToEdit={clientToEdit}
            />
          ) : (
            <CreateClientForm closeModal={closeModal} />
          )}
        </FormModal>
      )}
      {/* Delete confirmation modal */}
      <ModalUI
        modalSx={deleteConfirmation}
        open={deleteModalIsOpen}
        isCloseBtn={false}
        onCloseHandler={onCloseDeleteModal}
        className='confirmation-modal'
        modalTitle={t('alerts.titles.delete')}
      >
        <SimpleModalContent
          content={['alerts.confirm.client']}
          mainBtn='buttons.delete'
          onMainBtnClick={onDeleteClient}
          secondaryBtn='buttons.cancel'
          onSecondaryBtnClick={onCloseDeleteModal}
        />
      </ModalUI>
      {/* Filter modal */}
      {filterOpen && (
        <CustomMuiDataTableFilter
          isOpen={filterOpen}
          setFilterState={setFilterOpen}
          config={filtersConfig}
          filters={clientFilters}
          onSaveHandler={onSaveFilters}
          tableName='Clients'
        />
      )}
    </Box>
  );
};

const mapStateToProps = (state) => ({
  ...state,
});

const mapDispatchToProps = {
  getAllClients: clientActions.getAllClientsWithStats,
  updateClient: clientActions.updateClient,
  mergeClientAction: clientActions.mergeClients,
  deleteClient: clientActions.deleteClient,
  clearClients: clientActions.clearClients,
};

export default connect(mapStateToProps, mapDispatchToProps)(ClientsTable);
